import { tokenContract } from '@daymade/api-contracts';
import { ClientInferResponseBody } from '@ts-rest/core';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import { formattedTokenCost } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import MinigameCard from '../token-town/MinigameCard';
import GraphicWrapper from './GraphicWrapper';
import BackLink from './links/BackLink';
import MobileCarousel from './mobile-carousel';
import styles from './mobile-carousel/MobileCarousel.module.css';

const carouselSettings = {
  dots: true,
  slidesToShow: 2,
  slidesToScroll: 1,
  infinite: true,
  autoplay: true,
  autoplaySpeed: 3000,
  speed: 500,
  swipeToSlide: false,
  responsive: [
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

type TYouMightAlsoLikeOptionsResponse = ClientInferResponseBody<
  typeof tokenContract.public.getYouMightAlsoLikeOptions,
  200
>;
type TYouMightAlsoLikeOptionsItem = TYouMightAlsoLikeOptionsResponse[number];

interface IYouMightAlsoLikeSection {
  tokenBalance: number;
  backTo?: 'games' | 'prizes';
}

const YouMightAlsoLikeSection = observer(
  ({ tokenBalance, backTo }: IYouMightAlsoLikeSection) => {
    const [redemptionOptions, setRedemptionOptions] = useState<
      TYouMightAlsoLikeOptionsResponse | []
    >([]);
    const { currentUser } = useUserStore();
    const loggedIn = !!currentUser;

    useEffect(() => {
      async function handleGetRedemptionOptions() {
        if (!redemptionOptions.length) {
          const result =
            await tokenApiClient.public.getYouMightAlsoLikeOptions();

          if (result.status === HttpStatusCode.OK) {
            setRedemptionOptions(result.body);
          }
        }
      }
      handleGetRedemptionOptions();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getOnClickAction = (
      redemptionOption: TYouMightAlsoLikeOptionsItem,
    ) => {
      return Router.push(`/token-town/${redemptionOption.slug}`);
    };

    return (
      <GraphicWrapper imagePosition="bottom" imageOverlay={true}>
        <div className="youMightAlsoLikeSection fullWidth">
          <h2>You might also like</h2>
          <p className="text">A few more epic ways to redeem your tokens</p>

          {tokenBalance > 0 && (
            <div
              className="flexCentered flexRow fullWidth"
              style={{ marginTop: '0.5em' }}
            >
              <img
                className="tokenIcon"
                src={`${ASSET_PREFIX}/images/token-town/arcade-gold-token.svg`}
                alt=""
              />
              <p className="text" style={{ margin: '0 0.5em' }}>
                You have <strong>{formattedTokenCost(tokenBalance)}</strong>
              </p>
              <img
                className="tokenIcon"
                src={`${ASSET_PREFIX}/images/token-town/arcade-gold-token.svg`}
                alt=""
              />
            </div>
          )}

          <MobileCarousel
            settings={carouselSettings}
            desktopClass="gridDesktop"
            mobileClass="spacingMobile"
          >
            {redemptionOptions.map((redemptionOption, idx) => {
              return (
                <MinigameCard
                  key={`sz-${idx}`}
                  className={idx === 2 ? 'desktopOnly' : undefined}
                  redemptionOption={redemptionOption}
                  tokenBalance={tokenBalance}
                  onClick={() => getOnClickAction(redemptionOption)}
                  locked={!loggedIn}
                />
              );
            })}
          </MobileCarousel>

          <BackLink color="light" to={backTo} />

          <style jsx>
            {`
              .youMightAlsoLikeSection {
                display: flex;
                flex-direction: column;
                justify-content: center;
              }

              .youMightAlsoLikeSection h2 {
                font-family: 'Press Start 2P', 'Montserrat', sans-serif !important;
              }

              .text {
                color: white;
                text-align: center;
              }

              .tokenIcon {
                width: 10%;
                max-width: 35px;
              }

              :global(.slick-slider) {
                width: 100%;
              }

              :global(.slick-dots li button:before) {
                color: #fff !important;
              }

              :global(.slick-dots li.slick-active button:before) {
                color: #fff !important;
              }

              ${`.${styles.spacingMobile} .genericTokenRedemptionCard {
                margin: 0 10px;
                width: inherit !important;
              }`}

              @media (min-width: 700px) {
                .text {
                  font-size: 1.5em;
                }
              }
            `}
          </style>
        </div>
      </GraphicWrapper>
    );
  },
);

export default YouMightAlsoLikeSection;
